import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { useListing } from '../../contexts/ListingContext';
import { CARD_RARITIES } from '../../constants/filterConstants';
import SellIcon from '@mui/icons-material/Sell';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

/**
 * ListingQueue - Shows the listing queue in the topbar and manages card listings
 */
const ListingQueue = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const {
    listingItems,
    removeFromListing,
    updateListingPrice,
    updateListingCurrency,
    clearListing,
    listCards,
    getTotalListingValue,
    marketPrices,
    loading
  } = useListing();
  
  const [open, setOpen] = useState(false);
  const [listing, setListing] = useState(false);
  const [priceAdjustment, setPriceAdjustment] = useState(0); // Percentage adjustment
  const [showPriceAdjustment, setShowPriceAdjustment] = useState(true); // Open by default

  // Handle opening/closing the dialog
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Handle price change for a card
  const handlePriceChange = (uid, newPrice) => {
    const price = parseFloat(newPrice);
    if (!isNaN(price) && price > 0) {
      // Round to 4 decimal places to avoid floating point precision issues
      const roundedPrice = Math.round(price * 10000) / 10000;
      updateListingPrice(uid, roundedPrice);
    }
  };

  // Handle currency change for a card
  const handleCurrencyChange = (uid, currency) => {
    updateListingCurrency(uid, currency);
  };

  // Handle listing all cards
  const handleListAll = async () => {
    try {
      setListing(true);
      const result = await listCards();
      console.log('Listing successful:', result);
      handleClose();
    } catch (error) {
      console.error('Listing failed:', error);
      // TODO: Show error message to user
    } finally {
      setListing(false);
    }
  };

  // Get rarity info using existing filter constants
  const getRarityInfo = (item) => {
    // Try to get rarity from the item itself first, then from card details
    let rarity = item.rarity || item.card_rarity || item.details?.rarity;
    
    if (!rarity) {
      console.log('No rarity found in item:', item);
      return CARD_RARITIES[0]; // Fallback to common
    }
    
    const rarityData = CARD_RARITIES.find(r => r.value === rarity);
    if (!rarityData) {
      console.log('Rarity not found for value:', rarity, 'Available rarities:', CARD_RARITIES);
      return CARD_RARITIES[0]; // Fallback to common
    }
    return rarityData;
  };

  // Get the suggested price for a card (use low_price instead of 95%)
  const getSuggestedPrice = (item) => {
    const marketData = marketPrices[item.card_detail_id];
    return marketData?.low_price || 0.01; // Use low_price directly
  };

  // Handle bulk price adjustment
  const handleBulkPriceAdjustment = () => {
    if (priceAdjustment === 0) return;
    
    listingItems.forEach(item => {
      const marketData = marketPrices[item.card_detail_id];
      const basePrice = marketData?.low_price || item.listing_price;
      const adjustedPrice = basePrice * (1 + priceAdjustment / 100);
      const finalPrice = Math.max(adjustedPrice, 0.01); // Minimum 0.01
      // Round to 4 decimal places to avoid floating point precision issues
      const roundedPrice = Math.round(finalPrice * 10000) / 10000;
      updateListingPrice(item.uid, roundedPrice);
    });
    
    setShowPriceAdjustment(false);
    setPriceAdjustment(0);
  };

  // Get the total value with proper currency formatting
  const getTotalValueByurrency = () => {
    const usdItems = listingItems.filter(item => item.currency === 'USD' || !item.currency);
    const decItems = listingItems.filter(item => item.currency === 'DEC');
    
    const usdTotal = usdItems.reduce((total, item) => total + (item.listing_price || 0), 0);
    const decTotal = decItems.reduce((total, item) => total + (item.listing_price || 0), 0);
    
    if (usdTotal > 0 && decTotal > 0) {
      return `$${usdTotal.toFixed(4)} + ${decTotal.toFixed(4)} DEC`;
    } else if (decTotal > 0) {
      return `${decTotal.toFixed(4)} DEC`;
    } else {
      return `$${usdTotal.toFixed(4)}`;
    }
  };

  return (
    <>
      {/* Listing Queue Icon */}
      <Tooltip title={`Listing Queue (${listingItems.length} cards)`}>
        <IconButton onClick={handleOpen} sx={{ color: colors.grey[100] }}>
          <Badge badgeContent={listingItems.length} color="error">
            <LocalOfferIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Listing Queue Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
            borderRadius: 2,
            border: `1px solid ${colors.primary[300]}`
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            color: colors.grey[100],
            borderBottom: `1px solid ${colors.primary[300]}`,
            pb: 2,
            mb: 0
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">
              Listing Queue ({listingItems.length} cards)
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="h6" color={colors.greenAccent[400]}>
                Total Value: {getTotalValueByurrency()}
              </Typography>
              <Typography variant="h6" color={colors.yellow[400]}>
                Total Fees: {listingItems.length} DEC
              </Typography>
              {listingItems.length > 0 && (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => setShowPriceAdjustment(!showPriceAdjustment)}
                    sx={{
                      color: colors.blueAccent[400],
                      borderColor: colors.blueAccent[400],
                      '&:hover': {
                        backgroundColor: colors.blueAccent[800],
                        borderColor: colors.blueAccent[300]
                      }
                    }}
                  >
                    Adjust Prices
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearAllIcon />}
                    onClick={clearListing}
                    sx={{
                      color: colors.redAccent[400],
                      borderColor: colors.redAccent[400],
                      '&:hover': {
                        backgroundColor: colors.redAccent[800],
                        borderColor: colors.redAccent[300]
                      }
                    }}
                  >
                    Clear All
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Price Adjustment Panel */}
          {showPriceAdjustment && (
            <Paper 
              elevation={2}
              sx={{ 
                mb: 3, 
                p: 2, 
                backgroundColor: colors.primary[500], 
                borderRadius: 2,
                border: `1px solid ${colors.greenAccent[600]}`
              }}
            >
              <Typography variant="h6" color={colors.greenAccent[400]} gutterBottom>
                Bulk Price Adjustment
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  type="number"
                  label="Percentage (%)"
                  value={priceAdjustment}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || value === '-') {
                      setPriceAdjustment(0);
                    } else {
                      const parsed = parseFloat(value);
                      if (!isNaN(parsed)) {
                        setPriceAdjustment(parsed);
                      }
                    }
                  }}
                  size="small"
                  inputProps={{
                    min: -50,
                    max: 100,
                    step: 1
                  }}
            
                />
                <Typography variant="body2" color={colors.grey[300]}>
                  {priceAdjustment > 0 ? `+${priceAdjustment}%` : `${priceAdjustment}%`} from market low price
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleBulkPriceAdjustment}
                  disabled={priceAdjustment === 0}
                  sx={{
                    backgroundColor: colors.greenAccent[600],
                    '&:hover': {
                      backgroundColor: colors.greenAccent[700]
                    },
                    '&:disabled': {
                      backgroundColor: colors.grey[600]
                    }
                  }}
                >
                  Apply to All
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowPriceAdjustment(false)}
                  sx={{
                    color: colors.grey[300],
                    borderColor: colors.grey[300]
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Paper>
          )}

          {listingItems.length === 0 ? (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                backgroundColor: colors.blueAccent[800],
                color: colors.grey[100],
                '& .MuiAlert-icon': {
                  color: colors.blueAccent[400]
                }
              }}
            >
              No cards in listing queue. Select cards from your collection to add them here.
            </Alert>
          ) : (
            <TableContainer
              component={Paper}
              elevation={2}
              sx={{
                backgroundColor: colors.primary[400],
                borderRadius: 2,
                border: `1px solid ${colors.primary[300]}`,
                maxHeight: 400,
                overflowY: 'auto'
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      backgroundColor: colors.primary[300], 
                      color: colors.grey[100], 
                      fontWeight: 'bold',
                      borderBottom: `2px solid ${colors.greenAccent[600]}`
                    }}>
                      Card
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: colors.primary[300], 
                      color: colors.grey[100], 
                      fontWeight: 'bold',
                      borderBottom: `2px solid ${colors.greenAccent[600]}`
                    }}>
                      Level
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: colors.primary[300], 
                      color: colors.grey[100], 
                      fontWeight: 'bold',
                      borderBottom: `2px solid ${colors.greenAccent[600]}`
                    }}>
                      Rarity
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: colors.primary[300], 
                      color: colors.grey[100], 
                      fontWeight: 'bold',
                      borderBottom: `2px solid ${colors.greenAccent[600]}`
                    }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: colors.primary[300], 
                      color: colors.grey[100], 
                      fontWeight: 'bold',
                      borderBottom: `2px solid ${colors.greenAccent[600]}`
                    }}>
                      Currency
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: colors.primary[300], 
                      color: colors.grey[100], 
                      fontWeight: 'bold',
                      borderBottom: `2px solid ${colors.greenAccent[600]}`
                    }}>
                      Price
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: colors.primary[300], 
                      color: colors.grey[100], 
                      fontWeight: 'bold',
                      borderBottom: `2px solid ${colors.greenAccent[600]}`
                    }}>
                      Market Price
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: colors.primary[300], 
                      color: colors.grey[100], 
                      fontWeight: 'bold',
                      borderBottom: `2px solid ${colors.greenAccent[600]}`
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listingItems.map((item, index) => {
                    const marketData = marketPrices[item.card_detail_id];
                    const suggestedPrice = getSuggestedPrice(item);
                    const rarityInfo = getRarityInfo(item);
                    
                    return (
                      <TableRow
                        key={item.uid || index}
                        sx={{
                          backgroundColor: index % 2 === 0 ? colors.primary[450] : colors.primary[400],
                          '&:hover': {
                            backgroundColor: colors.primary[500]
                          },
                          borderBottom: `1px solid ${colors.primary[300]}`
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight="medium" color={colors.grey[100]}>
                            {item.name || 'Unknown Card'}
                          </Typography>
                          <Typography variant="caption" color={colors.greenAccent[400]}>
                            ID: {item.uid}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Chip
                            label={`Level ${item.level || 1}`}
                            size="small"
                            sx={{
                              backgroundColor: colors.greenAccent[600],
                              color: colors.grey[100],
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <img 
                              src={rarityInfo.icon} 
                              width={20}
                              height={20}
                            />
                            <Typography variant="body2" color={colors.grey[100]}>
                              {rarityInfo.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {item.gold && (
                              <Chip
                                label="Gold"
                                size="small"
                                sx={{
                                  backgroundColor: '#DAA520',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )}
                            {item.foil > 0 && (
                              <Chip
                                label={`Foil ${item.foil}`}
                                size="small"
                                sx={{
                                  backgroundColor: colors.blueAccent[600],
                                  color: colors.grey[100],
                                  fontWeight: 'bold'
                                }}
                              />
                            )}
                            {!item.gold && item.foil === 0 && (
                              <Chip
                                label="Regular"
                                size="small"
                                sx={{
                                  backgroundColor: colors.grey[600],
                                  color: colors.grey[100]
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                              value={item.currency || 'USD'}
                              onChange={(e) => handleCurrencyChange(item.uid, e.target.value)}
                            
                            >
                              <MenuItem value="USD">USD</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        
                        <TableCell>
                          <TextField
                            type="number"
                            value={parseFloat((item.listing_price || 0).toFixed(4))}
                            onChange={(e) => handlePriceChange(item.uid, e.target.value)}
                            size="small"
                            inputProps={{
                              min: 0.01,
                              step: 0.01
                            }}
                            InputProps={{
                              startAdornment: (
                                <Typography variant="body2" sx={{ color: colors.grey[400], mr: 0.5 }}>
                                  {item.currency === 'DEC' ? 'DEC' : '$'}
                                </Typography>
                              )
                            }}
                            sx={{
                              width: 120,
                              '& .MuiInputBase-input': {
                                color: colors.grey[100]
                              },
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: colors.primary[500],
                                '& fieldset': {
                                  borderColor: colors.primary[300]
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.greenAccent[400]
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.greenAccent[400]
                                }
                              }
                            }}
                          />
                        </TableCell>
                        
                        <TableCell>
                          {marketData ? (
                            <Box>
                              <Typography variant="body2" color={colors.grey[100]}>
                                Market Low: ${marketData.low_price?.toFixed(4)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color={colors.grey[400]}>
                              No market data
                            </Typography>
                          )}
                          <Typography variant="caption" color={colors.greenAccent[400]} sx={{ display: 'block', mt: 0.5 }}>
                            Fee: 1 DEC per card
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handlePriceChange(item.uid, suggestedPrice)}
                              sx={{
                                color: colors.greenAccent[400],
                                borderColor: colors.greenAccent[600],
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                                px: 1,
                                '&:hover': {
                                  backgroundColor: colors.greenAccent[800],
                                  borderColor: colors.greenAccent[400]
                                }
                              }}
                            >
                              ${suggestedPrice.toFixed(4)}
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => removeFromListing(item.uid)}
                              sx={{
                                color: colors.redAccent[400],
                                '&:hover': {
                                  backgroundColor: colors.redAccent[800]
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions 
          sx={{ 
            p: 3,
            borderTop: `1px solid ${colors.primary[300]}`,
            backgroundColor: colors.primary[450]
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              color: colors.grey[100],
              '&:hover': {
                backgroundColor: colors.primary[500]
              }
            }}
          >
            Close
          </Button>
          
          {listingItems.length > 0 && (
            <Button
              variant="contained"
              startIcon={listing ? <CircularProgress size={20} /> : <SellIcon />}
              onClick={handleListAll}
              disabled={listing || loading}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.greenAccent[700]
                },
                '&:disabled': {
                  backgroundColor: colors.grey[600]
                }
              }}
            >              {listing ? 'Listing...' : `List All Cards (${getTotalValueByurrency()} + ${listingItems.length} DEC fees)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ListingQueue;
